import { Color, Radius, Spacing } from 'zens-storybook'
import React, { Suspense, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ApiError, Draft, ProductData, useProductMutation, useStore } from 'zens-store'
import { useNumberParams, useRouter } from 'zens-router'
import { ProductForm, NavigationDelete, ProductHeader, LayoutHeader } from '@dms/component'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const ProductEdit = () => {
  const [confirmDelete, setConfirmDelete] = useState<Draft<ProductData>>()
  const [errors, setErrors] = useState<Record<string, string>>()
  const { message } = useStore()
  const [{ companyId, productId }] = useNumberParams()
  const mutationProduct = useProductMutation({ companyId })
  const router = useRouter()
  const { t } = useTranslation()

  const save = async (draft: Draft<ProductData>) => {
    try {
      setErrors({})
      const product = await mutationProduct.update(draft)
      if (!product) throw new Error('Product not updated')

      message.add({
        message: t('Device was updated successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`PRODUCT: Product failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Device could not be saved')}. ${errorMessage}`,
          status: 'error',
        })
      }
      return
    }
    router.push(`/companies/${companyId}/products/${productId}`)
  }

  const remove = async () => {
    try {
      await mutationProduct.delete(confirmDelete!)
      message.add({
        message: t('Device was deleted successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as Error
      console.debug(`PRODUCT: Product deletion failed: ${errorMessage}`)
      message.add({
        message: `${t('Device could not be deleted')}. ${errorMessage}`,
        status: 'error',
      })
      return
    }

    router.push(`/companies/${companyId}/products`)
  }

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton title breadcrumb />}>
        <ProductHeader companyId={companyId} productId={productId} breadcrumbItems={[{ title: t('Edit') }]} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<ProductForm.Skeleton />}>
          <ProductForm
            companyId={companyId}
            productId={productId}
            onSave={save}
            onDelete={async (draft) => setConfirmDelete(draft)}
            cancelHref={`/companies/${companyId}/products/${productId}`}
            errors={errors}
          />
        </Suspense>
        <NavigationDelete
          entity={t('Device')}
          visible={!!confirmDelete}
          onClose={() => setConfirmDelete(undefined)}
          onDelete={remove}
        />
      </View>
    </View>
  )
}

export default ProductEdit
